import type { Location, WeatherData, Alert, CurrentWeather, ApiResponse } from '@miweather/shared';

const API_BASE = '/api/v1';

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`);

  let json: ApiResponse<T>;
  try {
    json = await response.json();
  } catch {
    throw new Error(`Server returned non-JSON response (status: ${response.status})`);
  }

  if (!json.success) {
    throw new Error(json.error || 'API request failed');
  }

  return json.data;
}

// Locations
export async function getLocations(): Promise<{ cities: Location[]; counties: Location[] }> {
  return fetchApi('/locations');
}

export async function searchLocations(query: string): Promise<{ results: Location[] }> {
  return fetchApi(`/locations/search?q=${encodeURIComponent(query)}`);
}

export async function getLocation(id: string): Promise<Location> {
  return fetchApi(`/locations/${id}`);
}

export async function getLocationsByRegion(region: string): Promise<{ locations: Location[] }> {
  return fetchApi(`/locations/region/${region}`);
}

// Weather
export async function getWeather(locationId: string): Promise<WeatherData> {
  return fetchApi(`/weather/${locationId}`);
}

export async function getWeatherByCoordinates(lat: number, lon: number): Promise<WeatherData> {
  return fetchApi(`/weather/coordinates?lat=${lat}&lon=${lon}`);
}

export async function getCurrentWeather(locationId: string): Promise<{ location: Location; current: CurrentWeather }> {
  return fetchApi(`/weather/${locationId}/current`);
}

// Alerts
export async function getAlerts(): Promise<{ alerts: Alert[]; count: number; updatedAt: string }> {
  return fetchApi('/alerts');
}

export async function getAlertsBySeverity(severity: string): Promise<{ alerts: Alert[] }> {
  return fetchApi(`/alerts/by-severity?severity=${encodeURIComponent(severity)}`);
}

export async function getAlertsForCounty(countyId: string): Promise<{ alerts: Alert[] }> {
  return fetchApi(`/alerts/county/${countyId}`);
}
